import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, ChevronRight, CheckCircle, Upload,
  X, Camera, Save, ShieldCheck, XCircle
} from 'lucide-react';
import { getCEByEmail, getVendorsForRegion } from '../supportConfig';
import { getStatusConfig, normaliseStatus, isTicketDone } from '../ticketStatus';
import { relocStatusCfg } from './RelocationPage';

const TICKET_API = 'https://g3mu2eke0l.execute-api.ap-south-1.amazonaws.com/dev_waas_customersupport/ticket';

// Module-level constant — must be outside component to avoid minifier TDZ error
const PRESET_ISSUES = [
  { id: 'pump',     label: 'Pump Tamper',       icon: '🔧', desc: 'Pump tamper or malfunction detected' },
  { id: 'tamper',   label: 'Tamper Alert',       icon: '⚠️', desc: 'Physical tamper detected on device' },
  { id: 'recharge', label: 'Recharge Issue',     icon: '💳', desc: 'Customer unable to recharge or activate plan' },
  { id: 'filter',   label: 'Filter Replacement', icon: '💧', desc: 'One or more filters need replacement' },
  { id: 'leakage',  label: 'Water Leakage',      icon: '🚿', desc: 'Water leaking from device or pipes' },
  { id: 'nowater',  label: 'No Water Output',    icon: '🚫', desc: 'Device is powered but no water dispensed' },
  { id: 'other',    label: 'Other / Custom',      icon: '📝', desc: 'Describe the issue manually below' },
];

// ── Create Ticket Modal for CE — full device list + all required fields ─────────
function CreateTicketModal({ ce, onClose, onCreated }) {
  const [step,        setStep]      = useState(1);     // 1=device, 2=issue, 3=review
  // Step 1 state
  const [allDevices,  setAllDev]    = useState([]);
  const [loadingDev,  setLoadDev]   = useState(true);
  const [devSearch,   setDevSearch] = useState('');
  const [selectedDev, setSelDev]    = useState(null);
  // Step 2 state
  const [issueType,   setIssue]     = useState('');
  const [customIssue, setCustom]    = useState('');
  const [notes,       setNotes]     = useState('');
  const [priority,    setPriority]  = useState('MEDIUM');
  // Submit state
  const [submitting,  setSubmit]    = useState(false);
  const [error,       setError]     = useState('');
  const [done,        setDone]      = useState(false);
  const [newTicketId, setNewId]     = useState('');

  // Load all registered devices on mount
  useEffect(() => {
    setLoadDev(true);
    fetch('/api/admin-devices', { headers: { 'ngrok-skip-browser-warning': 'true' } })
      .then(r => r.json())
      .then(data => {
        const list = data.registrations || data.devices || (Array.isArray(data) ? data : []);
        setAllDev(list);
      })
      .catch(() => setAllDev([]))
      .finally(() => setLoadDev(false));
  }, []);

  // Filter devices by search query
  const filtered = allDevices.filter(d => {
    if (!devSearch.trim()) return true;
    const q = devSearch.toLowerCase();
    return (
      (d.friendly_name || '').toLowerCase().includes(q) ||
      (d.mac_id        || '').toLowerCase().includes(q) ||
      (d.Enduser_name  || '').toLowerCase().includes(q) ||
      (d.location      || '').toLowerCase().includes(q) ||
      (d.phone_no      || '').toLowerCase().includes(q)
    );
  });

  const getIssueText = () => {
    if (issueType === 'other') return customIssue.trim();
    return PRESET_ISSUES.find(p => p.id === issueType)?.label || issueType;
  };

  // Generate a unique TKT-XXXXXXXX id client-side (Lambda also generates one
  // if we don't send it, but sending guarantees we can show it in the success UI)
  const genTicketId = () => 'TKT-' + Math.random().toString(36).slice(2,10).toUpperCase();

  const handleSubmit = async () => {
    if (!getIssueText()) { setError('Please select or describe the issue'); return; }
    setSubmit(true); setError('');
    const ticketId = genTicketId();
    try {
      const body = {
        ticket_id:     ticketId,
        device:        selectedDev?.friendly_name || 'Unknown',
        macid:         selectedDev?.mac_id        || '',
        customer_name: selectedDev?.Enduser_name  || '',
        phone:         selectedDev?.phone_no      || '',
        location:      selectedDev?.location      || ce?.region || '',
        assigned_to:   '',
        issue:         getIssueText(),
        notes:         notes.trim(),
        priority,
        status:        'OPEN',
        ce_email:      ce?.email || '',
        ce_name:       ce?.name  || '',
        ce_id:         ce?.code  || '',
      };
      const res = await fetch('/api/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = await res.json();
      setNewId(data.ticket_id || ticketId);
      setDone(true);
      setTimeout(() => { onCreated(); onClose(); }, 2400);
    } catch (e) { setError(e.message); }
    finally { setSubmit(false); }
  };

  const inp = {
    width:'100%', padding:'10px 12px', borderRadius:10,
    border:'1.5px solid rgba(0,0,0,.1)', fontSize:13,
    fontFamily:'inherit', background:'#fafafa', outline:'none', boxSizing:'border-box',
  };

  const PRIORITY_CFG = {
    HIGH:   { color:'#FF3B30', bg:'rgba(255,59,48,.07)'   },
    MEDIUM: { color:'#E6870A', bg:'rgba(255,159,10,.07)'  },
    LOW:    { color:'#34C759', bg:'rgba(52,199,89,.07)'   },
  };

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.55)',
      backdropFilter:'blur(14px)',zIndex:400,display:'flex',alignItems:'center',justifyContent:'center',
      animation:'fadeUp .2s ease'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:24,padding:28,
        width:540,maxWidth:'95vw',maxHeight:'92vh',overflowY:'auto',
        boxShadow:'0 32px 80px rgba(0,0,0,.24)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif"}}>

        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:'#1c1c1e'}}>Create Ticket</div>
            <div style={{fontSize:11,color:'#8E8E93',marginTop:2}}>
              {done ? 'Done!' : `Step ${step} of 3 — ${['Select Device','Select Issue','Review & Submit'][step-1]}`}
            </div>
          </div>
          <button onClick={onClose} style={{width:28,height:28,borderRadius:'50%',border:'none',
            background:'rgba(0,0,0,.07)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <span style={{fontSize:14,color:'#636366'}}>✕</span>
          </button>
        </div>

        {/* Progress bar */}
        <div style={{height:4,background:'#f0f0f0',borderRadius:4,marginBottom:22}}>
          <div style={{height:'100%',borderRadius:4,
            width: done ? '100%' : `${(step/3)*100}%`,
            background:'linear-gradient(90deg,#7C3AED,#A855F7)',transition:'width .35s ease'}}/>
        </div>

        {/* ─── SUCCESS ─── */}
        {done && (
          <div style={{textAlign:'center',padding:'28px 0'}}>
            <div style={{width:76,height:76,borderRadius:'50%',margin:'0 auto 18px',
              background:'linear-gradient(135deg,#34C759,#248A3D)',
              display:'flex',alignItems:'center',justifyContent:'center',
              boxShadow:'0 10px 36px rgba(52,199,89,.4)'}}>
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
                <path d="M5 12l5 5L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={{fontSize:20,fontWeight:800,color:'#1c1c1e',marginBottom:6}}>Ticket Created!</div>
            <div style={{fontSize:13,color:'#7C3AED',fontWeight:700,fontFamily:'monospace'}}>{newTicketId}</div>
            <div style={{fontSize:12,color:'#8E8E93',marginTop:6}}>Support team has been notified</div>
          </div>
        )}

        {/* ─── STEP 1: DEVICE SELECT ─── */}
        {!done && step === 1 && (
          <>
            <div style={{fontSize:13,fontWeight:700,color:'#1c1c1e',marginBottom:10}}>
              🔍 Select Device
            </div>

            {/* Search */}
            <input value={devSearch} onChange={e=>setDevSearch(e.target.value)}
              placeholder="Search by name, MAC, customer…"
              style={{...inp, marginBottom:10}}/>

            {/* Device list */}
            <div style={{maxHeight:280,overflowY:'auto',display:'flex',
              flexDirection:'column',gap:6,marginBottom:14}}>

              {loadingDev && (
                <div style={{textAlign:'center',padding:24,color:'#8E8E93',fontSize:12}}>
                  <div style={{width:22,height:22,borderRadius:'50%',border:'3px solid rgba(124,58,237,.15)',
                    borderTopColor:'#7C3AED',animation:'spin .7s linear infinite',margin:'0 auto 8px'}}/>
                  Loading devices…
                </div>
              )}

              {!loadingDev && filtered.length === 0 && (
                <div style={{textAlign:'center',padding:20,color:'#8E8E93',fontSize:12}}>
                  No devices found
                </div>
              )}

              {!loadingDev && filtered.map(d => {
                const sel = selectedDev?.mac_id === d.mac_id;
                return (
                  <div key={d.mac_id} onClick={() => setSelDev(d)} style={{
                    padding:'10px 13px',borderRadius:12,cursor:'pointer',
                    border: sel ? '2px solid #7C3AED' : '1.5px solid rgba(0,0,0,.08)',
                    background: sel ? 'rgba(124,58,237,.05)' : '#fafafa',
                    transition:'all .15s',
                  }}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color: sel?'#7C3AED':'#1c1c1e',
                          overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {d.friendly_name || d.mac_id}
                        </div>
                        <div style={{fontSize:10,color:'#8E8E93',marginTop:2,display:'flex',gap:8,flexWrap:'wrap'}}>
                          <span style={{fontFamily:'monospace'}}>{d.mac_id?.toUpperCase()}</span>
                          {d.Enduser_name && <span>👤 {d.Enduser_name}</span>}
                          {d.location     && <span>📍 {d.location}</span>}
                          {d.phone_no     && <span>📞 {d.phone_no}</span>}
                        </div>
                      </div>
                      {sel && <span style={{color:'#7C3AED',fontSize:16,flexShrink:0,marginLeft:8}}>✓</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedDev && (
              <div style={{padding:'10px 13px',borderRadius:12,
                background:'rgba(124,58,237,.06)',border:'1.5px solid rgba(124,58,237,.2)',marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:'#7C3AED',marginBottom:4}}>✅ Selected</div>
                <div style={{fontSize:13,fontWeight:700,color:'#1c1c1e'}}>{selectedDev.friendly_name}</div>
                <div style={{fontSize:11,color:'#8E8E93',marginTop:2}}>
                  MAC: {selectedDev.mac_id?.toUpperCase()} · {selectedDev.Enduser_name} · {selectedDev.location}
                </div>
              </div>
            )}

            {!selectedDev && (
              <div style={{fontSize:11,color:'#8E8E93',marginBottom:14,padding:'8px 12px',
                background:'rgba(0,0,0,.025)',borderRadius:10}}>
                ℹ️ You can skip device selection and still create a ticket
              </div>
            )}

            <button onClick={() => setStep(2)} style={{
              width:'100%',padding:'13px',borderRadius:14,border:'none',
              background:'linear-gradient(135deg,#7C3AED,#6D28D9)',color:'#fff',
              fontWeight:700,fontSize:15,cursor:'pointer',fontFamily:'inherit',
              boxShadow:'0 4px 14px rgba(124,58,237,.35)'}}>
              Next →
            </button>
          </>
        )}

        {/* ─── STEP 2: ISSUE + NOTES + PRIORITY ─── */}
        {!done && step === 2 && (
          <>
            <div style={{fontSize:13,fontWeight:700,color:'#1c1c1e',marginBottom:12}}>
              🎯 Select Issue
            </div>

            <div style={{display:'flex',flexDirection:'column',gap:7,marginBottom:14}}>
              {PRESET_ISSUES.map(p => {
                const sel = issueType === p.id;
                return (
                  <div key={p.id} onClick={() => setIssue(p.id)} style={{
                    display:'flex',alignItems:'center',gap:11,
                    padding:'10px 13px',borderRadius:12,cursor:'pointer',
                    border: sel ? '2px solid #7C3AED' : '1.5px solid rgba(0,0,0,.07)',
                    background: sel ? 'rgba(124,58,237,.05)' : '#fafafa',
                    transition:'all .15s',
                  }}>
                    <span style={{fontSize:20}}>{p.icon}</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:700,
                        color:sel?'#7C3AED':'#1c1c1e'}}>{p.label}</div>
                      <div style={{fontSize:10,color:'#8E8E93'}}>{p.desc}</div>
                    </div>
                    {sel && <span style={{color:'#7C3AED',fontSize:15}}>✓</span>}
                  </div>
                );
              })}
            </div>

            {issueType === 'other' && (
              <div style={{marginBottom:12}}>
                <label style={{fontSize:11,fontWeight:600,color:'#48484a',display:'block',marginBottom:5}}>
                  Describe the issue *
                </label>
                <textarea value={customIssue} onChange={e=>setCustom(e.target.value)}
                  rows={2} placeholder="Describe what the customer is experiencing…"
                  style={{...inp,resize:'vertical'}}/>
              </div>
            )}

            <div style={{marginBottom:12}}>
              <label style={{fontSize:11,fontWeight:600,color:'#48484a',display:'block',marginBottom:5}}>
                Additional Notes
              </label>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)}
                rows={2} placeholder="Any extra details, observations, steps taken…"
                style={{...inp,resize:'vertical'}}/>
            </div>

            <div style={{marginBottom:16}}>
              <label style={{fontSize:11,fontWeight:600,color:'#48484a',display:'block',marginBottom:7}}>
                Priority
              </label>
              <div style={{display:'flex',gap:8}}>
                {['HIGH','MEDIUM','LOW'].map(p => {
                  const cfg = PRIORITY_CFG[p];
                  return (
                    <button key={p} onClick={()=>setPriority(p)} style={{
                      flex:1,padding:'9px',borderRadius:10,cursor:'pointer',fontFamily:'inherit',
                      border: priority===p ? `2px solid ${cfg.color}` : '1.5px solid rgba(0,0,0,.08)',
                      background: priority===p ? cfg.bg : '#fafafa',
                      fontSize:12,fontWeight:700,color:cfg.color,
                    }}>{p}</button>
                  );
                })}
              </div>
            </div>

            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setStep(1)} style={{
                flex:1,padding:'12px',borderRadius:14,border:'1.5px solid rgba(0,0,0,.1)',
                background:'#fff',color:'#636366',fontWeight:600,fontSize:14,cursor:'pointer',fontFamily:'inherit'}}>
                ← Back
              </button>
              <button onClick={()=>{
                if (!issueType) { setError('Please select an issue type'); return; }
                if (issueType==='other' && !customIssue.trim()) { setError('Please describe the issue'); return; }
                setError(''); setStep(3);
              }} style={{
                flex:2,padding:'12px',borderRadius:14,border:'none',
                background:'linear-gradient(135deg,#7C3AED,#6D28D9)',color:'#fff',
                fontWeight:700,fontSize:14,cursor:'pointer',fontFamily:'inherit',
                boxShadow:'0 4px 14px rgba(124,58,237,.35)'}}>
                Review →
              </button>
            </div>
            {error && <div style={{color:'#FF3B30',fontSize:12,marginTop:8,textAlign:'center'}}>⚠️ {error}</div>}
          </>
        )}

        {/* ─── STEP 3: REVIEW ─── */}
        {!done && step === 3 && (
          <>
            <div style={{fontSize:13,fontWeight:700,color:'#1c1c1e',marginBottom:14}}>
              📋 Review & Submit
            </div>

            <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:18}}>
              {[
                { label:'Device',        value: selectedDev?.friendly_name || 'Not selected',  mono:false },
                { label:'MAC ID',        value: selectedDev?.mac_id?.toUpperCase() || '—',       mono:true  },
                { label:'Customer',      value: selectedDev?.Enduser_name  || '—',              mono:false },
                { label:'Phone',         value: selectedDev?.phone_no      || '—',              mono:false },
                { label:'Location',      value: selectedDev?.location || ce?.region || '—',    mono:false },
                { label:'Issue',         value: getIssueText() || '—',                         mono:false },
                { label:'Notes',         value: notes.trim()   || '(none)',                    mono:false },
                { label:'Priority',      value: priority,                                      mono:false },
                { label:'Support Eng.',  value: `${ce?.code||''} · ${ce?.name||''}`.trim()||'—', mono:false },
              ].map(r => (
                <div key={r.label} style={{display:'flex',justifyContent:'space-between',
                  padding:'8px 13px',background:'rgba(0,0,0,.025)',borderRadius:10}}>
                  <span style={{fontSize:11,color:'#8E8E93',fontWeight:500,flexShrink:0}}>{r.label}</span>
                  <span style={{fontSize:12,color:'#1c1c1e',fontWeight:600,textAlign:'right',
                    maxWidth:'62%',wordBreak:'break-all',
                    fontFamily:r.mono?'monospace':'inherit'}}>{r.value}</span>
                </div>
              ))}
            </div>

            {error && (
              <div style={{color:'#FF3B30',fontSize:12,marginBottom:12,padding:'9px 12px',
                background:'rgba(255,59,48,.06)',borderRadius:9,textAlign:'center'}}>
                ⚠️ {error}
              </div>
            )}

            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>{setError('');setStep(2);}} style={{
                flex:1,padding:'12px',borderRadius:14,border:'1.5px solid rgba(0,0,0,.1)',
                background:'#fff',color:'#636366',fontWeight:600,fontSize:14,cursor:'pointer',fontFamily:'inherit'}}>
                ← Back
              </button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                flex:2,padding:'12px',borderRadius:14,border:'none',
                background:submitting?'#c7c7cc':'linear-gradient(135deg,#7C3AED,#6D28D9)',
                color:'#fff',fontWeight:700,fontSize:14,
                cursor:submitting?'wait':'pointer',fontFamily:'inherit',
                boxShadow:submitting?'none':'0 4px 14px rgba(124,58,237,.35)',
                display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                {submitting
                  ? (<><div style={{width:14,height:14,borderRadius:'50%',
                      border:'2px solid rgba(255,255,255,.3)',borderTopColor:'#fff',
                      animation:'spin .7s linear infinite'}}/> Creating…</>)
                  : '✅ Create Ticket'
                }
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

const STYLES = `
  @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin   { to{transform:rotate(360deg)} }
  .ce-ticket { transition:background .15s,box-shadow .15s; cursor:pointer; }
  .ce-ticket:hover { background:#fafafa!important; box-shadow:0 4px 16px rgba(0,0,0,.08)!important; }
`;

const fmtDate = iso => {
  if (!iso) return '—';
  try { const d=new Date(iso); return d.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})+' '+d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true}); }
  catch { return iso; }
};

function StatusBadge({ status }) {
  const s = getStatusConfig(status);
  return <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,
    background:s.bg,color:s.color,display:'inline-flex',alignItems:'center',gap:4,whiteSpace:'nowrap'}}>
    <span style={{width:5,height:5,borderRadius:'50%',background:s.dot}}/>{s.label}
  </span>;
}

// ── Ticket Detail (CE View) ───────────────────────────────────────────────────
function CETicketDetail({ ticket, ce, vendors, onClose, onUpdate }) {
  const [notes,     setNotes]     = useState('');
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [error,     setError]     = useState('');

  const status = normaliseStatus(ticket.status);
  const isDone = isTicketDone(ticket.status);

  const handleSaveNote = async () => {
    if (!notes.trim()) { setError('Add a note before saving.'); return; }
    setSaving(true); setError(''); setSaved(false);
    try {
      const res = await fetch(`/api/tickets`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_id:   ticket.ticket_id,
          notes:       notes.trim(),
        }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      setSaved(true);
      setTimeout(() => { onUpdate(); onClose(); }, 700);
    } catch(e) { setError(e.message); }
    finally    { setSaving(false); }
  };

  const handleClose = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`/api/tickets`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_id:   ticket.ticket_id,
          status:      'CLOSED',
          resolved_at: new Date().toISOString(),
          notes:       notes.trim() || ticket.notes || 'Closed by CE',
        }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      setSaved(true);
      setTimeout(() => { onUpdate(); onClose(); }, 700);
    } catch(e) { setError(e.message); }
    finally    { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',
      backdropFilter:'blur(14px)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',
      animation:'fadeUp .2s ease'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:22,padding:26,
        width:540,maxWidth:'95vw',maxHeight:'90vh',overflowY:'auto',
        boxShadow:'0 32px 80px rgba(0,0,0,.22)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif"}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14}}>
          <div>
            <div style={{fontSize:10,color:'#aeaeb2',fontFamily:'monospace',fontWeight:700,
              letterSpacing:.8,marginBottom:5}}>{ticket.ticket_id}</div>
            <div style={{fontSize:17,fontWeight:800,color:'#1c1c1e',lineHeight:1.3}}>{ticket.issue}</div>
          </div>
          <button onClick={onClose} style={{width:28,height:28,borderRadius:'50%',border:'none',
            background:'rgba(0,0,0,.07)',cursor:'pointer',display:'flex',alignItems:'center',
            justifyContent:'center',flexShrink:0}}><X size={12}/></button>
        </div>
        <div style={{display:'flex',gap:8,marginBottom:16}}>
          <StatusBadge status={ticket.status}/>
        </div>

        {/* Info */}
        {[
          ['Customer',    ticket.customer_name || '—'],
          ['Device',      ticket.device || '—'],
          ['MAC ID',      ticket.macid || '—'],
          ['Location',    ticket.location || '—'],
          ['Manager',     ticket.assigned_agent_name || '—'],
        ].map(([l,v])=>(
          <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'8px 13px',
            background:'rgba(0,0,0,.025)',borderRadius:10,marginBottom:4}}>
            <span style={{fontSize:12,color:'#8E8E93'}}>{l}</span>
            <span style={{fontSize:13,color:'#1c1c1e',fontWeight:600,
              fontFamily:l==='MAC ID'?'monospace':'inherit'}}>{v}</span>
          </div>
        ))}

        {ticket.notes && (
          <div style={{marginTop:10,padding:'12px 14px',background:'#fafafa',borderRadius:10,
            border:'1px solid rgba(0,0,0,.06)'}}>
            <div style={{fontSize:10,color:'#8E8E93',fontWeight:700,letterSpacing:.6,
              textTransform:'uppercase',marginBottom:5}}>Latest Note</div>
            <div style={{fontSize:13,color:'#48484a',lineHeight:1.5}}>{ticket.notes}</div>
          </div>
        )}

        {/* CE Actions */}
        {!isDone && (
          <div style={{marginTop:18,padding:18,background:'rgba(175,82,222,.04)',
            borderRadius:14,border:'1px solid rgba(175,82,222,.15)'}}>
            <div style={{fontSize:11,color:'#AF52DE',fontWeight:700,letterSpacing:.8,
              textTransform:'uppercase',marginBottom:14}}>CE Actions</div>

            <label style={{fontSize:11,fontWeight:600,color:'#48484a',display:'block',marginBottom:5}}>
              Update Note
            </label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)}
              placeholder="Findings, vendor progress, steps taken..." rows={3}
              style={{width:'100%',padding:'10px 12px',borderRadius:10,
                border:'1.5px solid rgba(0,0,0,.12)',fontSize:13,background:'#f9f9f9',
                resize:'vertical',outline:'none',marginBottom:12,lineHeight:1.5,fontFamily:'inherit'}}/>

            {error && <div style={{fontSize:12,color:'#FF3B30',padding:'9px 12px',
              background:'rgba(255,59,48,.05)',borderRadius:9,marginBottom:12}}>{error}</div>}

            <div style={{display:'flex',gap:10}}>
              <button onClick={handleSaveNote} disabled={saving||saved} style={{flex:1,padding:12,
                borderRadius:12,border:'1.5px solid rgba(175,82,222,.3)',
                background:'rgba(175,82,222,.06)',
                color:'#AF52DE',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',
                display:'flex',alignItems:'center',justifyContent:'center',gap:8,opacity:saving?.7:1}}>
                <Save size={14}/>Save Note
              </button>
              <button onClick={handleClose} disabled={saving||saved} style={{flex:1,padding:12,
                borderRadius:12,border:'none',
                background:saved?'linear-gradient(135deg,#34C759,#248A3D)':'linear-gradient(135deg,#AF52DE,#8B36B0)',
                color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',
                display:'flex',alignItems:'center',justifyContent:'center',gap:8,opacity:saving?.7:1}}>
                {saved ? <><CheckCircle size={14}/>Saved!</> : <><CheckCircle size={14}/>Close Ticket</>}
              </button>
            </div>
          </div>
        )}

        {isDone && (
          <div style={{marginTop:16,padding:'14px 16px',background:'rgba(52,199,89,.07)',
            borderRadius:12,border:'1px solid rgba(52,199,89,.2)',
            display:'flex',alignItems:'center',gap:10}}>
            <CheckCircle size={20} color="#34C759"/>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:'#248A3D'}}>Ticket Closed</div>
            </div>
          </div>
        )}

        <button onClick={onClose} style={{width:'100%',marginTop:12,padding:11,borderRadius:12,
          border:'1.5px solid rgba(0,0,0,.09)',background:'#fff',color:'#636366',
          fontWeight:600,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Close</button>
      </div>
    </div>
  );
}

// ── Main CE Page ──────────────────────────────────────────────────────────────
// ── CE: Relocation Requests panel ────────────────────────────────
function RelocationCEPanel({ ce }) {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [busyId,   setBusyId]   = useState(null);

  const fetchRequests = useCallback(async () => {
    if (!ce?.email) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/relocation`);
      const data = await res.json();
      const mine = (Array.isArray(data) ? data : []).filter(r =>
        (r.assignedCEEmail || '').toLowerCase() === ce.email.toLowerCase());
      setRequests(mine);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [ce]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const updateStatus = async (requestId, status) => {
    setBusyId(requestId);
    try {
      await fetch(`/api/relocation/${encodeURIComponent(requestId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      fetchRequests();
    } catch (e) { console.error(e); }
    finally { setBusyId(null); }
  };

  return (
    <div style={{ animation:'fadeUp .3s ease' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div>
          <div style={{ fontSize:16, fontWeight:800, color:'#1c1c1e' }}>🚚 Relocation Requests</div>
          <p style={{ color:'#8E8E93', fontSize:13, marginTop:2 }}>{requests.length} assigned to you</p>
        </div>
        <button onClick={fetchRequests} disabled={loading} style={{
          padding:'8px 16px', borderRadius:10, border:'1.5px solid rgba(0,0,0,.1)',
          background:'#fff', color:'#AF52DE', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
        }}>{loading ? 'Loading...' : '↻ Refresh'}</button>
      </div>

      {requests.length === 0 ? (
        <div style={{ textAlign:'center', padding:40, background:'#fff', borderRadius:14,
          border:'1px solid rgba(0,0,0,.06)', color:'#8E8E93', fontSize:13 }}>
          No relocation requests assigned to you yet
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {requests.map((r,i) => {
            const cfg = relocStatusCfg(r.status);
            const busy = busyId === r.requestId;
            return (
              <div key={r.requestId||i} style={{background:'#fff',borderRadius:14,padding:'14px 18px',
                boxShadow:'0 1px 6px rgba(0,0,0,.06)',border:'1px solid rgba(0,0,0,.06)'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}>
                  <span style={{fontSize:10,color:'#aeaeb2',fontFamily:'monospace',fontWeight:700}}>{r.requestId}</span>
                  <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,
                    background:cfg.bg,color:cfg.color}}>{r.status||'Pending'}</span>
                </div>
                <div style={{fontSize:14,fontWeight:700,color:'#1c1c1e',marginBottom:4}}>{r.userName}</div>
                <div style={{fontSize:12,color:'#48484a',marginBottom:4}}>📞 {r.contactNumber||'—'}</div>
                <div style={{fontSize:11,color:'#8E8E93',marginBottom:10,lineHeight:1.5}}>
                  <div><strong>From:</strong> {r.currentAddress||'—'}</div>
                  <div><strong>To:</strong> {r.relocationAddress||'—'}</div>
                </div>
                {r.status === 'Assigned to CE' && (
                  <button onClick={()=>updateStatus(r.requestId,'In Progress')} disabled={busy} style={{
                    padding:'8px 16px',borderRadius:10,border:'none',
                    background:'linear-gradient(135deg,#FF9F0A,#CC7A00)',color:'#fff',
                    fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit',opacity:busy?.7:1}}>
                    {busy?'Updating...':'Start (In Progress)'}
                  </button>
                )}
                {r.status === 'In Progress' && (
                  <div style={{display:'flex',gap:8}}>
                    <button onClick={()=>updateStatus(r.requestId,'Completed')} disabled={busy} style={{
                      flex:1,padding:'8px 16px',borderRadius:10,border:'none',
                      background:'linear-gradient(135deg,#34C759,#248A3D)',color:'#fff',
                      fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit',opacity:busy?.7:1}}>
                      {busy?'Updating...':'Mark Completed'}
                    </button>
                    <button onClick={()=>updateStatus(r.requestId,'Rejected')} disabled={busy} style={{
                      padding:'8px 16px',borderRadius:10,border:'1.5px solid rgba(255,59,48,.25)',
                      background:'rgba(255,59,48,.05)',color:'#FF3B30',
                      fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit',opacity:busy?.7:1}}>
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Work Summary Graph ────────────────────────────────────────────────────────────────────────────
function CEWorkGraph({ tickets }) {
  // Only closed tickets that have both created_at and end_time (or resolved_at / closed_at)
  const closed = tickets.filter(t => {
    const done = isTicketDone(t.status);
    const hasEnd = t.end_time || t.resolved_at || t.closed_at || t.updated_at;
    return done && t.created_at && hasEnd;
  });

  if (closed.length === 0) {
    return (
      <div style={{background:'#fff',borderRadius:16,padding:'16px 18px',
        border:'1px solid rgba(0,0,0,.06)',boxShadow:'0 2px 8px rgba(0,0,0,.04)',marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:'#1c1c1e',marginBottom:4}}>⏱ Work Summary</div>
        <div style={{fontSize:12,color:'#8E8E93'}}>No completed tickets yet — resolution times will appear here once tickets are closed</div>
      </div>
    );
  }

  // Build bar data: hours to resolve per ticket
  const bars = closed.map(t => {
    const start = new Date(t.created_at).getTime();
    const end   = new Date(t.end_time || t.resolved_at || t.closed_at || t.updated_at).getTime();
    const hrs   = Math.max(0.1, Math.round(((end - start) / 36e5) * 10) / 10);
    return { id: t.ticket_id, issue: t.issue, hrs };
  }).sort((a,b) => a.hrs - b.hrs); // sort fastest first

  const maxHrs  = Math.max(...bars.map(b => b.hrs));
  const avgHrs  = Math.round((bars.reduce((s,b) => s+b.hrs,0) / bars.length) * 10) / 10;
  const chartH  = 100;
  const gridLines = 4;

  const fmtHrs = h => h < 1 ? `${Math.round(h*60)}m` : h < 24 ? `${h}h` : `${Math.round(h/24)}d`;
  const barColor = h => h <= 24 ? '#34C759' : h <= 72 ? '#FF9F0A' : '#FF3B30';

  return (
    <div style={{background:'#fff',borderRadius:16,padding:'16px 18px',
      border:'1px solid rgba(0,0,0,.06)',boxShadow:'0 2px 8px rgba(0,0,0,.04)',marginBottom:20}}>

      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:'#1c1c1e'}}>⏱ Work Summary</div>
          <div style={{fontSize:11,color:'#8E8E93',marginTop:2}}>Resolution time per closed ticket</div>
        </div>
        <div style={{display:'flex',gap:14,fontSize:11}}>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:18,fontWeight:800,color:'#7C3AED'}}>{closed.length}</div>
            <div style={{color:'#8E8E93'}}>Resolved</div>
          </div>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:18,fontWeight:800,color: avgHrs<=24?'#34C759':avgHrs<=72?'#FF9F0A':'#FF3B30'}}>{fmtHrs(avgHrs)}</div>
            <div style={{color:'#8E8E93'}}>Avg time</div>
          </div>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:18,fontWeight:800,color:'#34C759'}}>{fmtHrs(bars[0].hrs)}</div>
            <div style={{color:'#8E8E93'}}>Fastest</div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{position:'relative',paddingLeft:36}}>
        {/* Y-axis grid */}
        {Array.from({length:gridLines+1}).map((_,i) => {
          const val = Math.round((maxHrs / gridLines) * (gridLines - i));
          return (
            <div key={i} style={{position:'absolute',left:0,right:0,
              top:`${(i/gridLines)*chartH}px`,display:'flex',alignItems:'center'}}>
              <span style={{fontSize:8,color:'#C7C7CC',minWidth:30,textAlign:'right',paddingRight:5}}>
                {fmtHrs(val)}
              </span>
              <div style={{flex:1,borderTop:'1px dashed rgba(0,0,0,.07)'}}/>
            </div>
          );
        })}
        {/* Bars */}
        <div style={{display:'flex',alignItems:'flex-end',gap:6,height:chartH+28,overflowX:'auto'}}>
          {bars.map(b => {
            const barH = Math.max(4, (b.hrs / maxHrs) * chartH);
            const color = barColor(b.hrs);
            return (
              <div key={b.id} style={{display:'flex',flexDirection:'column',
                alignItems:'center',gap:3,minWidth:36,flexShrink:0,height:'100%',justifyContent:'flex-end'}}>
                {/* Value label */}
                <span style={{fontSize:8,fontWeight:700,color,marginBottom:2}}>{fmtHrs(b.hrs)}</span>
                {/* Bar */}
                <div title={`${b.issue} — ${fmtHrs(b.hrs)}`} style={{
                  width:28,borderRadius:'5px 5px 0 0',
                  background:`linear-gradient(180deg,${color},${color}bb)`,
                  height:`${barH}px`,transition:'height .4s ease',
                  boxShadow:`0 2px 8px ${color}44`,
                }}/>
                {/* X-label */}
                <div style={{fontSize:8,color:'#8E8E93',fontWeight:600,
                  textAlign:'center',whiteSpace:'nowrap',overflow:'hidden',
                  textOverflow:'ellipsis',maxWidth:36,marginTop:4}}>
                  {b.id.replace('TKT-','')}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{display:'flex',gap:14,marginTop:8,justifyContent:'center'}}>
        {[['#34C759','≤24h (Fast)'],['#FF9F0A','24–72h (Normal)'],['#FF3B30','>72h (Slow)']].map(([c,l]) => (
          <span key={l} style={{fontSize:10,color:'#8E8E93',display:'flex',alignItems:'center',gap:4}}>
            <span style={{width:8,height:8,borderRadius:2,background:c,display:'inline-block'}}/>{l}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function CustomerEngineer({ session }) {
  const [tickets,   setTickets]   = useState([]);
  const [ce,        setCe]        = useState(null);
  const [vendors,   setVendors]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [filter,    setFilter]    = useState('All');
  const [selected,  setSelected]  = useState(null);
  const [showCreate,setShowCreate]= useState(false);
  const [activeTab, setActiveTab] = useState('tickets');

  useEffect(() => {
    const email = session?.getIdToken()?.payload?.email || '';
    const tokenName = session?.getIdToken()?.payload?.name || '';
    const ceObj = getCEByEmail(email);
    if (ceObj) {
      setCe(ceObj);
      setVendors(getVendorsForRegion(ceObj.region));
    } else if (email) {
      // Support Engineer created via Role Management but not in the static
      // supportConfig.js list — fall back gracefully (name from Cognito, no
      // region/vendor data yet, but tickets still load by ce_email below).
      const fallbackName = tokenName || email.split('@')[0].replace(/[._]/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      setCe({ id: null, name: fallbackName, email, region: 'Unassigned' });
      setVendors([]);
    }
  }, [session]);

  const fetchTickets = useCallback(async () => {
    if (!ce) return;
    setLoading(true);
    try {
      const res  = await fetch(`/api/tickets?ce_email=${encodeURIComponent(ce.email)}`,
        {headers:{'ngrok-skip-browser-warning':'true'}});
      const data = await res.json();
      setTickets(data.tickets || (Array.isArray(data) ? data : []));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [ce]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const filters = ['All','Open','Closed'];
  const filtered = filter === 'All' ? tickets : tickets.filter(t => {
    const s = normaliseStatus(t.status);
    if (filter === 'Open')   return !isTicketDone(s);
    if (filter === 'Closed') return isTicketDone(s);
    return true;
  });

  const open = tickets.filter(t => !isTicketDone(t.status)).length;
  const done = tickets.filter(t => isTicketDone(t.status)).length;

  return (
    <div style={{fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",maxWidth:900}}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',
        marginBottom:24,flexWrap:'wrap',gap:12}}>
        <div>
          <img src="/havells-logo.png" alt="Havells" style={{height:44,objectFit:'contain',marginBottom:10,display:'block'}}/>
          <h1 style={{fontSize:26,fontWeight:800,color:'#1c1c1e',letterSpacing:-.5,margin:0}}>
            Support Engineer
          </h1>
          <p style={{color:'#8E8E93',fontSize:13,marginTop:4}}>
            {ce?.code ? `${ce.code} · ` : ''}{ce?.name} · {ce?.region} · {vendors.length} vendors available
          </p>
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <button onClick={()=>setShowCreate(true)} style={{
            display:'inline-flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:12,
            border:'none',background:'linear-gradient(135deg,#7C3AED,#6D28D9)',color:'#fff',
            fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',
            boxShadow:'0 4px 14px rgba(124,58,237,.35)'}}>
            ＋ Create Ticket
          </button>
          <button onClick={fetchTickets} disabled={loading} style={{
            display:'inline-flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:12,
            border:'none',background:'linear-gradient(135deg,#AF52DE,#8B36B0)',color:'#fff',
            fontWeight:600,fontSize:13,cursor:'pointer',fontFamily:'inherit',
            boxShadow:'0 4px 14px rgba(175,82,222,.3)',opacity:loading?.7:1}}>
            <RefreshCw size={13} style={loading?{animation:'spin .8s linear infinite'}:{}}/>
            {loading?'Loading...':'Refresh'}
          </button>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{display:'flex',gap:4,background:'#f0f4f8',borderRadius:12,padding:3,marginBottom:20,
        width:'fit-content'}}>
        {[{id:'tickets',label:'Tickets'},{id:'relocation',label:'Relocation'}].map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{padding:'8px 18px',borderRadius:9,
            border:'none',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit',
            background:activeTab===t.id?'#fff':'transparent',
            color:activeTab===t.id?'#AF52DE':'#8E8E93',
            boxShadow:activeTab===t.id?'0 1px 6px rgba(0,0,0,.09)':'none',transition:'all .18s'}}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'relocation' ? (
        <RelocationCEPanel ce={ce}/>
      ) : (
      <>

      {/* Summary */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
        {[
          { label:'Total',  value:tickets.length, color:'#1c1c1e' },
          { label:'Open',   value:open,            color:'#FF9F0A' },
          { label:'Closed', value:done,             color:'#34C759' },
        ].map(s=>(
          <div key={s.label} style={{background:'#fff',borderRadius:14,padding:'14px 16px',
            boxShadow:'0 2px 8px rgba(0,0,0,.05)',border:'1px solid rgba(0,0,0,.06)',
            position:'relative',overflow:'hidden',animation:'fadeUp .35s ease both'}}>
            <div style={{position:'absolute',top:0,left:0,right:0,height:3,
              background:s.color,borderRadius:'14px 14px 0 0',opacity:.6}}/>
            <div style={{fontSize:24,fontWeight:800,color:'#1c1c1e',letterSpacing:-.4}}>{s.value}</div>
            <div style={{fontSize:12,color:'#8E8E93',marginTop:2}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ⏱ Work Summary Graph — resolution time per closed ticket */}
      <CEWorkGraph tickets={tickets}/>

      {/* Filter tabs */}
      <div style={{display:'flex',gap:4,background:'#f0f4f8',borderRadius:12,padding:3,
        marginBottom:18,width:'fit-content',flexWrap:'wrap'}}>
        {filters.map(f=>(
          <button key={f} onClick={()=>setFilter(f)} style={{padding:'7px 14px',borderRadius:9,
            border:'none',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',
            background:filter===f?'#fff':'transparent',
            color:filter===f?'#AF52DE':'#8E8E93',
            boxShadow:filter===f?'0 1px 6px rgba(0,0,0,.09)':'none',transition:'all .18s',whiteSpace:'nowrap'}}>
            {f}
          </button>
        ))}
      </div>

      {/* Ticket list */}
      {loading && tickets.length===0 ? (
        <div style={{textAlign:'center',padding:60,color:'#8E8E93'}}>
          <div style={{width:28,height:28,borderRadius:'50%',border:'3px solid rgba(175,82,222,.15)',
            borderTopColor:'#AF52DE',animation:'spin .7s linear infinite',margin:'0 auto 14px'}}/>
          <p style={{fontSize:13}}>Loading tickets...</p>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {filtered.length === 0 ? (
            <div style={{textAlign:'center',padding:60,color:'#8E8E93',fontSize:13}}>
              No tickets in this category
            </div>
          ) : filtered.map((t,i)=>{
            const st = getStatusConfig(t.status);
            const isDone = isTicketDone(t.status);
            return (
              <div key={t.ticket_id||i} className="ce-ticket" onClick={()=>setSelected(t)}
                style={{background:'#fff',borderRadius:14,padding:'14px 18px',
                  boxShadow:'0 1px 6px rgba(0,0,0,.06)',
                  border:'1px solid rgba(0,0,0,.06)',
                  display:'flex',alignItems:'center',gap:14,opacity:isDone?.7:1,
                  animation:`fadeUp .3s ease ${i*.04}s both`}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:st.dot,flexShrink:0}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:5,flexWrap:'wrap'}}>
                    <span style={{fontSize:10,color:'#aeaeb2',fontFamily:'monospace',fontWeight:700}}>{t.ticket_id}</span>
                    <StatusBadge status={t.status}/>
                  </div>
                  <div style={{fontSize:14,fontWeight:700,color:'#1c1c1e',marginBottom:4,
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.issue}</div>
                  <div style={{fontSize:11,color:'#8E8E93',display:'flex',gap:12,flexWrap:'wrap'}}>
                    <span>📍 {t.location||'—'}</span>
                    <span>👤 Manager: {t.assigned_agent_name||'—'}</span>
                    <span>🕐 {fmtDate(t.updated_at)}</span>
                  </div>
                </div>
                <ChevronRight size={15} color="#C7C7CC"/>
              </div>
            );
          })}
        </div>
      )}

      {selected && <CETicketDetail key={selected.ticket_id} ticket={selected} ce={ce} vendors={vendors}
        onClose={()=>setSelected(null)} onUpdate={()=>{ setSelected(null); fetchTickets(); }}/>}
      {showCreate && <CreateTicketModal ce={ce} onClose={()=>setShowCreate(false)} onCreated={fetchTickets}/>}
      </>
      )}
    </div>
  );
}
